import React from 'react';
import { ChangeEvent } from 'react';
import { Box, FormControlLabel, FormGroup, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { MessageGroupEditor } from '@shared/components/MessageGroupEditor/MessageGroupEditor';
import { StyledErrorText } from '@shared/components/errorText';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import {
  AGENT_IDLE_STATE_DELAY_MS_MAX,
  AGENT_IDLE_STATE_DELAY_MS_MIN,
  CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MAX,
  CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MIN,
  CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
  CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
} from '@features/Admin/Agent/model/idleState';
import { ExpandableSettingsBlock } from './SettingsBlocks';
import { IdleStatePanelProps } from './types';

export const IdleAndCustomerSettingsPanel: React.FC<IdleStatePanelProps> = ({
  draft,
  errors,
  isReadOnly,
  onDraftChange,
  expanded,
  onExpandedChange,
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ExpandableSettingsBlock
        id="idle"
        expanded={expanded.idle}
        onChange={(nextExpanded) => onExpandedChange('idle', nextExpanded)}
        header={
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Typography fontSize="16px" fontWeight={600}>
              {t('adminMainPage.agent.idleState.title')}
            </Typography>
            <FormGroup>
              <FormControlLabel
                sx={{ marginRight: 0 }}
                onClick={(event: React.MouseEvent) => event.stopPropagation()}
                control={
                  <CustomSwitch
                    checked={draft.enabled}
                    disabled={isReadOnly}
                    inputProps={{
                      'aria-label': t('adminMainPage.agent.idleState.title'),
                    }}
                    onClick={(event: React.MouseEvent) => event.stopPropagation()}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      onDraftChange({ enabled: e.target.checked });
                      onExpandedChange('idle', e.target.checked);
                    }}
                  />
                }
                label=""
              />
            </FormGroup>
          </Stack>
        }>
        <Stack spacing={2}>
          <CustomTextField
            label={t('adminMainPage.agent.idleState.delayLabel')}
            type="number"
            fullWidth
            disabled={isReadOnly || !draft.enabled}
            inputProps={{
              min: AGENT_IDLE_STATE_DELAY_MS_MIN,
              max: AGENT_IDLE_STATE_DELAY_MS_MAX,
              step: 1,
            }}
            value={draft.delayMs}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onDraftChange({ delayMs: event.target.value })
            }
          />
          {errors.delayMs && <StyledErrorText>{errors.delayMs}</StyledErrorText>}

          <MessageGroupEditor
            groups={['idle']}
            value={{ idle: draft.messages }}
            disabled={isReadOnly || !draft.enabled}
            onChange={(value) => onDraftChange({ messages: value.idle ?? [] })}
            labels={{
              helperText: t('adminMainPage.agent.idleState.messagesHelper'),
              emptyGroupText: t('adminMainPage.agent.idleState.messagesEmpty'),
              addMessageLabel: t('adminMainPage.agent.idleState.addMessage'),
              messagePlaceholder: t('adminMainPage.agent.idleState.messagePlaceholder'),
              groupLabels: { idle: t('adminMainPage.agent.idleState.messagesGroup') },
              deleteMessageAriaLabel: (_group, index) =>
                t('adminMainPage.agent.idleState.deleteMessage', { index }),
            }}
          />
          {errors.messages && <StyledErrorText>{errors.messages}</StyledErrorText>}
        </Stack>
      </ExpandableSettingsBlock>

      <ExpandableSettingsBlock
        id="customerIdle"
        expanded={expanded.customerIdle}
        onChange={(nextExpanded) => onExpandedChange('customerIdle', nextExpanded)}
        header={
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Typography fontSize="16px" fontWeight={600}>
              {t('adminMainPage.agent.idleState.customerTitle')}
            </Typography>
          </Stack>
        }>
        <Stack spacing={2}>
          <Typography color="text.secondary" fontSize="14px">
            {t('adminMainPage.agent.idleState.customerHelper')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 240 }}>
              <CustomTextField
                label={t('adminMainPage.agent.idleState.replyTimeoutLabel')}
                type="number"
                fullWidth
                disabled={isReadOnly}
                inputProps={{
                  min: CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
                  max: CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
                  step: 0.1,
                }}
                value={draft.customerReplyTimeoutSec}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onDraftChange({ customerReplyTimeoutSec: event.target.value })
                }
              />
              {errors.customerReplyTimeoutSec && (
                <StyledErrorText>{errors.customerReplyTimeoutSec}</StyledErrorText>
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 240 }}>
              <CustomTextField
                label={t('adminMainPage.agent.idleState.silenceThresholdLabel')}
                type="number"
                fullWidth
                disabled={isReadOnly}
                inputProps={{
                  min: CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
                  max: CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
                  step: 0.1,
                }}
                value={draft.customerSilenceThresholdSec}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onDraftChange({ customerSilenceThresholdSec: event.target.value })
                }
              />
              {errors.customerSilenceThresholdSec && (
                <StyledErrorText>{errors.customerSilenceThresholdSec}</StyledErrorText>
              )}
            </Box>
          </Box>

          <CustomTextField
            label={t('adminMainPage.agent.idleState.maxAttemptsLabel')}
            type="number"
            fullWidth
            disabled={isReadOnly}
            inputProps={{
              min: CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MIN,
              max: CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MAX,
              step: 1,
            }}
            value={draft.customerMaxAttempts}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onDraftChange({ customerMaxAttempts: event.target.value })
            }
          />
          {errors.customerMaxAttempts && (
            <StyledErrorText>{errors.customerMaxAttempts}</StyledErrorText>
          )}

          <MessageGroupEditor
            groups={['customerSilence']}
            value={{ customerSilence: draft.customerSilenceMessages }}
            disabled={isReadOnly}
            onChange={(value) =>
              onDraftChange({ customerSilenceMessages: value.customerSilence ?? [] })
            }
            labels={{
              helperText: t('adminMainPage.agent.idleState.customerMessagesHelper'),
              emptyGroupText: t('adminMainPage.agent.idleState.customerMessagesEmpty'),
              addMessageLabel: t('adminMainPage.agent.idleState.addCustomerMessage'),
              messagePlaceholder: t('adminMainPage.agent.idleState.customerMessagePlaceholder'),
              groupLabels: {
                customerSilence: t('adminMainPage.agent.idleState.customerMessagesGroup'),
              },
              deleteMessageAriaLabel: (_group, index) =>
                t('adminMainPage.agent.idleState.deleteCustomerMessage', { index }),
            }}
          />

          <MessageGroupEditor
            groups={['customerEndCall']}
            value={{ customerEndCall: draft.customerEndCallMessages }}
            disabled={isReadOnly}
            onChange={(value) =>
              onDraftChange({ customerEndCallMessages: value.customerEndCall ?? [] })
            }
            labels={{
              helperText: t('adminMainPage.agent.idleState.customerEndCallMessagesHelper'),
              emptyGroupText: t('adminMainPage.agent.idleState.customerEndCallMessagesEmpty'),
              addMessageLabel: t('adminMainPage.agent.idleState.addCustomerEndCallMessage'),
              messagePlaceholder: t(
                'adminMainPage.agent.idleState.customerEndCallMessagePlaceholder',
              ),
              groupLabels: {
                customerEndCall: t('adminMainPage.agent.idleState.customerEndCallMessagesGroup'),
              },
              deleteMessageAriaLabel: (_group, index) =>
                t('adminMainPage.agent.idleState.deleteCustomerEndCallMessage', { index }),
            }}
          />
        </Stack>
      </ExpandableSettingsBlock>
    </Box>
  );
};
